import { useState, useRef, useCallback, useEffect } from "react";
import { Camera, X, Loader2, Image as ImageIcon, FlipHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useStaticTranslation } from "@/lib/staticTranslations";

export interface VisualMatch {
  productHandle: string;
  productTitle: string;
  productImage: string;
  productPrice: string;
  similarityScore: number;
  matchReason: string;
}

interface ImageSearchUploaderProps {
  onAnalyzeComplete: (searchTerms: string[], description: string, visualMatches?: VisualMatch[]) => void;
  onCancel: () => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
}

interface AnalyzeResult {
  searchTerms: string[];
  description: string;
  category: string;
  confidence: number;
  visualMatches?: VisualMatch[];
  totalCompared?: number;
  error?: string;
}

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/heic", "image/heif"];

// Live camera capture component
function CameraCapture({ onCapture, onCancel, t }: { onCapture: (dataUrl: string) => void; onCancel: () => void; t: (key: string, fallback: string) => string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");

  const startCamera = useCallback(async (mode: "user" | "environment") => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: mode }, audio: false });
      setStream(s);
      setError(null);
    } catch {
      setError(t("cameraError", "לא ניתן לגשת למצלמה. אנא אפשר גישה בהגדרות הדפדפן."));
    }
  }, [t]);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      setStream(prev => { prev?.getTracks().forEach(t => t.stop()); return null; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const handleFlip = useCallback(async () => {
    stream?.getTracks().forEach(t => t.stop());
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newMode }, audio: false });
      setStream(s);
      setError(null);
    } catch {
      setError(t("switchCameraError", "לא ניתן להחליף מצלמה."));
    }
  }, [facingMode, stream, t]);

  const handleCapture = useCallback(() => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      if (facingMode === "user") {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(videoRef.current, 0, 0);
    }
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    stream?.getTracks().forEach(t => t.stop());
    onCapture(dataUrl);
  }, [facingMode, stream, onCapture]);

  const handleCancel = useCallback(() => {
    stream?.getTracks().forEach(t => t.stop());
    onCancel();
  }, [stream, onCancel]);

  return (
    <div className="space-y-3">
      {error ? (
        <div className="p-3 rounded border border-destructive/30 bg-destructive/10 text-destructive text-xs text-center">
          {error}
        </div>
      ) : (
        <div className="relative aspect-video rounded-lg overflow-hidden border border-border bg-black max-h-[50vh] w-full">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: facingMode === "user" ? "scaleX(-1)" : "none" }}
          />
          <button
            onClick={handleFlip}
            className="absolute top-2 left-2 rounded-full p-1.5 bg-background/70 hover:bg-background transition-colors"
            title={t("flipCamera", "החלף מצלמה")}
          >
            <FlipHorizontal className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleCancel} className="flex-1">
          {t("cancel", "ביטול")}
        </Button>
        {!error && (
          <Button size="sm" onClick={handleCapture} className="flex-1 gap-2">
            <Camera className="h-4 w-4" />
            {t("capture", "צלם")}
          </Button>
        )}
      </div>
    </div>
  );
}

export const ImageSearchUploader = ({
  onAnalyzeComplete,
  onCancel,
  isAnalyzing,
  setIsAnalyzing,
}: ImageSearchUploaderProps) => {
  const { t } = useStaticTranslation("imageSearch");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState<string>("");
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const compressImage = useCallback(async (dataUrl: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = img.width;
        let height = img.height;
        const maxDim = 800;
        if (width > height && width > maxDim) {
          height = (height * maxDim) / width;
          width = maxDim;
        } else if (height > maxDim) {
          width = (width * maxDim) / height;
          height = maxDim;
        }
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        let quality = 0.7;
        let base64 = canvas.toDataURL("image/jpeg", quality);
        while (base64.length > 500000 && quality > 0.2) {
          quality -= 0.1;
          base64 = canvas.toDataURL("image/jpeg", quality);
        }
        resolve(base64);
      };
      img.src = dataUrl;
    });
  }, []);

  const analyzeImage = useCallback(async (base64Image: string) => {
    setIsAnalyzing(true);
    setProgress(5);
    setAnalysisStatus(t("preparing", "מכין תמונה..."));

    try {
      const progressSteps = [
        { value: 10, text: t("identifyingType", "מזהה את סוג התכשיט...") },
        { value: 25, text: t("searchingProducts", "מחפש מוצרים מתאימים...") },
        { value: 45, text: t("loadingImages", "טוען תמונות מוצרים...") },
        { value: 60, text: t("comparingImages", "משווה תמונות...") },
        { value: 75, text: t("rankingSimilarity", "מדרג לפי דמיון...") },
        { value: 85, text: t("finishing", "מסיים עיבוד...") },
      ];

      let stepIndex = 0;
      const progressInterval = setInterval(() => {
        if (stepIndex < progressSteps.length) {
          setProgress(progressSteps[stepIndex].value);
          setAnalysisStatus(progressSteps[stepIndex].text);
          stepIndex++;
        }
      }, 5000);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-jewelry-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ image: base64Image, mode: "visual" }),
        }
      );

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) throw new Error(t("systemBusy", "המערכת עמוסה. נסה שוב בעוד מספר דקות."));
        if (response.status === 402) throw new Error(t("serviceError", "יש בעיה בשירות. נסה שוב מאוחר יותר."));
        throw new Error(errorData.error || t("analyzeError", "שגיאה בניתוח התמונה"));
      }

      setProgress(95);
      setAnalysisStatus(t("done", "מסיים..."));

      const result: AnalyzeResult = await response.json();

      if (result.error && result.confidence === 0) throw new Error(result.error);

      setProgress(100);
      const matchCount = result.visualMatches?.length || 0;
      setAnalysisStatus(matchCount > 0 ? `${matchCount} ${t("foundProducts", "מוצרים דומים!")}` : `${t("identified", "זוהה:")} ${result.category}`);

      await new Promise((resolve) => setTimeout(resolve, 500));
      onAnalyzeComplete(result.searchTerms, result.description, result.visualMatches);
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast({
        title: t("error", "שגיאה"),
        description: error instanceof Error ? error.message : t("analyzeError", "שגיאה בניתוח התמונה"),
        variant: "destructive",
      });
      setIsAnalyzing(false);
      setProgress(0);
      setAnalysisStatus("");
    }
  }, [onAnalyzeComplete, setIsAnalyzing, t]);

  const handleDataUrl = useCallback(async (dataUrl: string) => {
    try {
      setIsAnalyzing(true);
      setProgress(2);
      setAnalysisStatus(t("processingImage", "מעבד תמונה..."));
      const compressed = await compressImage(dataUrl);
      setPreviewUrl(compressed);
      await analyzeImage(compressed);
    } catch (error) {
      console.error("Error processing image:", error);
      toast({
        title: t("error", "שגיאה"),
        description: t("imageProcessError", "שגיאה בעיבוד התמונה, נסה תמונה אחרת"),
        variant: "destructive",
      });
      setIsAnalyzing(false);
      setProgress(0);
      setAnalysisStatus("");
    }
  }, [compressImage, analyzeImage, setIsAnalyzing, t]);

  const handleFile = useCallback(async (file: File) => {
    const isImage = file.type.startsWith("image/") ||
      ACCEPTED_TYPES.some(t => file.type.includes(t.split("/")[1]));
    if (!isImage) {
      toast({ title: t("unsupportedFile", "קובץ לא נתמך"), description: t("uploadImageFile", "יש להעלות קובץ תמונה"), variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      handleDataUrl(dataUrl);
    };
    reader.readAsDataURL(file);
  }, [handleDataUrl, t]);

  const handleCameraCapture = useCallback((dataUrl: string) => {
    setShowCamera(false);
    handleDataUrl(dataUrl);
  }, [handleDataUrl]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleClear = useCallback(() => {
    setPreviewUrl(null);
    setProgress(0);
    setAnalysisStatus("");
    setIsAnalyzing(false);
    setShowCamera(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [setIsAnalyzing]);

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {showCamera && !previewUrl && !isAnalyzing && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onCancel={() => setShowCamera(false)}
          t={t}
        />
      )}

      {previewUrl ? (
        <div className="relative space-y-3">
          <div className="relative aspect-square max-w-48 w-full mx-auto rounded-lg overflow-hidden border border-border">
            <img src={previewUrl} alt={t("searchImage", "תמונה לחיפוש")} className="w-full h-full object-cover" />
            {!isAnalyzing && (
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2 h-7 w-7 bg-background/80 hover:bg-background"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            {isAnalyzing && (
              <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}
          </div>

          {isAnalyzing && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-center text-muted-foreground">{analysisStatus}</p>
              <p className="text-xs text-center text-muted-foreground/70">{t("visualSearchTime", "החיפוש הויזואלי עשוי לקחת עד דקה")}</p>
            </div>
          )}
        </div>
      ) : !showCamera ? (
        <>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-all cursor-pointer",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            )}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">{t("dragImageHere", "גרור תמונה לכאן")}</p>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {t("orClickToSelect", "או לחץ לבחירת קובץ מהמכשיר")}
                </p>
              </div>
              <p className="text-xs text-primary/70 mt-1">
                {t("advancedVisualSearch", "חיפוש ויזואלי מתקדם — מוצא מוצרים דומים לפי תמונה")}
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCamera(true)}
              className="gap-2"
            >
              <Camera className="h-4 w-4" />
              {t("takePhoto", "צלם תמונה")}
            </Button>
          </div>
        </>
      ) : null}

      {!showCamera && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="w-full"
          disabled={isAnalyzing}
        >
          {t("cancel", "ביטול")}
        </Button>
      )}
    </div>
  );
};
